import type { CreateEnhanceProvider } from '../../types/plugin';
import type { ListPluginPropertyOptions } from './types';
import type { SlateProps } from 'slate-react';
import { useEffect } from 'react';

import { ListProvider } from './hooks/useCurrentList';
import { PicklistProvider } from 'domains/reporter/Template/usePicklist';
import { RecorderProvider, useSendModifyDictationMessage } from 'common/Recorder/useRecorder';

type ProviderComponentWithListProps = Readonly<{
  children: React.ReactNode;
  sendModifyDictationMessageRef: ListPluginPropertyOptions['sendModifyDictationMessageRef'];
}>;

export const ProviderComponentWithList = ({
  children,
  sendModifyDictationMessageRef,
}: ProviderComponentWithListProps): React.ReactElement => {
  const sendModifyDictationMessage = useSendModifyDictationMessage();

  useEffect(() => {
    if (sendModifyDictationMessageRef == null) return;

    sendModifyDictationMessageRef.current = sendModifyDictationMessage;

    return () => {
      sendModifyDictationMessageRef.current = null;
    };
  }, [sendModifyDictationMessage, sendModifyDictationMessageRef]);

  return <ListProvider>{children}</ListProvider>;
};

export const enhanceProviderList: CreateEnhanceProvider<ListPluginPropertyOptions> =
  ({ sendModifyDictationMessageRef }) =>
  (Component: React.ComponentType<SlateProps>) =>
    function EnhanceProviderList(props: SlateProps) {
      return (
        <Component {...props}>
          <RecorderProvider>
            <PicklistProvider>
              <ProviderComponentWithList
                sendModifyDictationMessageRef={sendModifyDictationMessageRef}
              >
                {props.children}
              </ProviderComponentWithList>
            </PicklistProvider>
          </RecorderProvider>
        </Component>
      );
    };
